import React, { useEffect, useState, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { X, Copy, Image, Video, Download, Trash2, Maximize2, HelpCircle, Pencil, Sparkles, Layers, FlaskConical } from 'lucide-react';
import imageGenerationService, {
  GeneratedImage,
  CreativeAnalysis,
  ImageStyle,
  StyleRating,
} from '../../services/api/imageGenerationService';
import { useCreativeAnalysis } from '../../hooks/useCreativeAnalysis';
import {
  CreativeIntentSection,
  CreativeIntentStyleRow,
  CreativeAnalysisLoadingIndicator,
} from './CreativeIntentSection';
import StyleFeedbackButtons from './StyleFeedbackButtons';

export interface BannerDetailPanelProps {
  image: GeneratedImage & { creative_analysis?: CreativeAnalysis | null };
  onClose: () => void;
  onUsePrompt?: () => void;
  onUseImage: () => void;
  onGenerateStyleVariations?: () => void;
  onConvertToVideo?: () => void;
  videoActionLabel?: string;
  videoActionDisabled?: boolean;
  onImageEdit?: () => void;
  onResize: () => void;
  /** レイヤー分解パネルを開く。未指定ならボタンを出さない */
  onLayers?: () => void;
  /** このバナーを起点にオートABテスト（自動改善ループ）を開始する。未指定ならボタンを出さない */
  onAutoAbTest?: () => void;
  onDownload: () => void;
  onDelete: () => void;
  showPromptButton?: boolean;
  onStyleRated?: (rating: StyleRating | null) => void;
  /** 見出しの下に出すタグの並び */
  tags?: React.ReactNode;
}

/**
 * Get color for a score value (0-10 scale)
 * 8.0+: green, 6.0-7.9: amber, <6.0: red
 */
const getScoreColor = (score: number): string => {
  if (score >= 7.5) return '#10B981'; // emerald-500
  if (score >= 5.0) return '#F59E0B'; // amber-500
  return '#EF4444'; // red-500
};

/**
 * Score progress bar component
 */
const ScoreBar: React.FC<{ label: string; score: number; maxScore?: number }> = ({
  label,
  score,
  maxScore = 10,
}) => {
  const percentage = (score / maxScore) * 100;
  const color = getScoreColor(score);

  return (
    <div className="mb-3">
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm text-gray-600">{label}</span>
        <span className="text-sm font-semibold" style={{ color }}>
          {score.toFixed(1)}
        </span>
      </div>
      <div className="w-full bg-gray-100 rounded-full h-2">
        <div
          className="h-2 rounded-full transition-all duration-500"
          style={{ width: `${percentage}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
};

/**
 * Tooltip component for AI effect prediction explanation
 */
const EffectPredictionTooltip: React.FC = () => {
  const [show, setShow] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const [arrowSide, setArrowSide] = useState<'left' | 'right'>('left');

  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const tooltipWidth = 288; // w-72 = 18rem = 288px
    const gap = 8;

    // Try right side first, fall back to left if it would overflow
    if (rect.right + gap + tooltipWidth < window.innerWidth) {
      setTooltipStyle({
        position: 'fixed',
        left: rect.right + gap,
        top: rect.top,
        zIndex: 9999,
      });
      setArrowSide('left');
    } else {
      setTooltipStyle({
        position: 'fixed',
        left: rect.left - gap - tooltipWidth,
        top: rect.top,
        zIndex: 9999,
      });
      setArrowSide('right');
    }
  }, []);

  useEffect(() => {
    if (!show) return;
    updatePosition();
    const handleScroll = () => updatePosition();
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [show, updatePosition]);

  const tooltipContent = show ? ReactDOM.createPortal(
    <div
      ref={tooltipRef}
      className="w-72 bg-gray-900 text-white text-xs rounded-lg shadow-xl p-4 leading-relaxed"
      style={{ ...tooltipStyle, pointerEvents: 'none' }}
    >
      <div className="font-bold text-sm mb-2">AI効果予測とは？</div>
      <p className="mb-3">
        AI効果予測は、過去の広告配信データを学習した独自モデルにより、
        表示ポテンシャルとクリエイティブ品質4指標を統合し、
        広告の成果期待値を数値化する分析指標です。
      </p>
      <div className="mb-2 font-semibold">【評価項目】</div>
      <ul className="mb-3 space-y-0.5 pl-1">
        <li>・インプレッション（表示回数）</li>
        <li>・視認性</li>
        <li>・理解度</li>
        <li>・コピー</li>
        <li>・行動促進</li>
      </ul>
      <div className="mb-2 font-semibold">【スコア基準】</div>
      <ul className="mb-3 space-y-0.5 pl-1">
        <li><span className="text-emerald-400 font-medium">9.0以上</span>：最高水準</li>
        <li><span className="text-emerald-400 font-medium">8.0以上</span>：高品質</li>
        <li><span className="text-amber-400 font-medium">7.0以上</span>：標準水準</li>
      </ul>
      <p className="mb-3 text-gray-300">※7.0未満は改善余地が大きい状態です。</p>
      <div className="mb-2 font-semibold">【注意事項】</div>
      <p className="text-gray-300">
        本分析は過去データと統計傾向に基づく予測です。
        実際の成果を保証するものではありません。
        媒体環境や競合状況により結果は変動します。
      </p>
      {/* Arrow */}
      {arrowSide === 'left' ? (
        <div className="absolute left-[-6px] top-2 w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-r-[6px] border-r-gray-900" />
      ) : (
        <div className="absolute right-[-6px] top-2 w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-l-[6px] border-l-gray-900" />
      )}
    </div>,
    document.body
  ) : null;

  return (
    <div className="relative inline-flex">
      <button
        ref={buttonRef}
        type="button"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onClick={() => setShow(!show)}
        className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
        aria-label="AI効果予測について"
      >
        <HelpCircle className="w-4 h-4" />
      </button>
      {tooltipContent}
    </div>
  );
};

/**
 * Compact style indicator for the banner detail header. Shows the style name
 * as a link-like chip; hovering (or tapping) it lazily loads the full style
 * record and reveals a rich tooltip with the sample image and attributes
 * (category / aspect ratio / platforms). No navigation — the hover detail is
 * the whole interaction.
 */
const StyleInfoBadge: React.FC<{ styleId: number; styleName: string }> = ({
  styleId,
  styleName,
}) => {
  const [show, setShow] = useState(false);
  const [detail, setDetail] = useState<ImageStyle | null>(null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const [arrowSide, setArrowSide] = useState<'left' | 'right'>('left');

  // Fetch the full style record once, on first interaction.
  const ensureDetail = useCallback(() => {
    if (detail || status === 'loading') return;
    setStatus('loading');
    imageGenerationService
      .getStyle(styleId)
      .then((res) => {
        setDetail(res.data);
        setStatus('idle');
      })
      .catch(() => setStatus('error'));
  }, [detail, status, styleId]);

  const updatePosition = useCallback(() => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const tooltipWidth = 288; // w-72 = 18rem
    const gap = 8;

    // Prefer the right side, fall back to the left when it would overflow.
    if (rect.right + gap + tooltipWidth < window.innerWidth) {
      setTooltipStyle({ position: 'fixed', left: rect.right + gap, top: rect.top, zIndex: 9999 });
      setArrowSide('left');
    } else {
      setTooltipStyle({
        position: 'fixed',
        left: rect.left - gap - tooltipWidth,
        top: rect.top,
        zIndex: 9999,
      });
      setArrowSide('right');
    }
  }, []);

  useEffect(() => {
    if (!show) return;
    updatePosition();
    const handleReposition = () => updatePosition();
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);
    return () => {
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [show, updatePosition]);

  const open = () => {
    ensureDetail();
    setShow(true);
  };

  const tooltipContent = show
    ? ReactDOM.createPortal(
        <div
          className="w-72 bg-gray-900 text-white text-xs rounded-lg shadow-xl"
          style={{ ...tooltipStyle, pointerEvents: 'none' }}
        >
          {/* Sample image (or skeleton while loading) */}
          {detail?.example_image_url ? (
            <img
              src={detail.example_image_url}
              alt={`${styleName}スタイルのサンプル`}
              className="w-full h-32 object-cover rounded-t-lg"
            />
          ) : status === 'loading' ? (
            <div className="w-full h-32 bg-gray-800 rounded-t-lg animate-pulse" />
          ) : null}

          <div className="p-4 leading-relaxed">
            <div className="font-bold text-sm mb-1">{detail?.name ?? styleName}スタイル</div>

            {status === 'loading' && !detail && (
              <p className="text-gray-400">スタイル情報を読み込み中…</p>
            )}
            {status === 'error' && (
              <p className="text-gray-400">スタイル情報を取得できませんでした。</p>
            )}

            {detail && (
              <>
                <div className="space-y-1">
                  {detail.category && (
                    <div className="flex gap-2">
                      <span className="text-gray-500 shrink-0">カテゴリ</span>
                      <span className="text-gray-200">{detail.category}</span>
                    </div>
                  )}
                  {detail.aspect_ratio && (
                    <div className="flex gap-2">
                      <span className="text-gray-500 shrink-0">アスペクト比</span>
                      <span className="text-gray-200">{detail.aspect_ratio}</span>
                    </div>
                  )}
                  {detail.platforms && detail.platforms.length > 0 && (
                    <div className="flex gap-2">
                      <span className="text-gray-500 shrink-0">媒体</span>
                      <span className="text-gray-200">{detail.platforms.join('、')}</span>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Arrow */}
          {arrowSide === 'left' ? (
            <div className="absolute left-[-6px] top-2 w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-r-[6px] border-r-gray-900" />
          ) : (
            <div className="absolute right-[-6px] top-2 w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-l-[6px] border-l-gray-900" />
          )}
        </div>,
        document.body
      )
    : null;

  return (
    <div className="relative inline-flex">
      <button
        ref={anchorRef}
        type="button"
        onMouseEnter={open}
        onMouseLeave={() => setShow(false)}
        onFocus={open}
        onBlur={() => setShow(false)}
        onClick={() => (show ? setShow(false) : open())}
        className="group inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 transition-colors"
        aria-label={`${styleName}スタイルの詳細`}
      >
        <Sparkles className="w-3 h-3 shrink-0" />
        <span className="underline decoration-dotted underline-offset-2 group-hover:decoration-solid">
          {styleName}スタイル
        </span>
      </button>
      {tooltipContent}
    </div>
  );
};

const BannerDetailPanel: React.FC<BannerDetailPanelProps> = ({
  image,
  onClose,
  onUsePrompt,
  onUseImage,
  onGenerateStyleVariations,
  onConvertToVideo,
  videoActionLabel = '動画バナー',
  videoActionDisabled = false,
  onImageEdit,
  onResize,
  onLayers,
  onAutoAbTest,
  onDownload,
  onDelete,
  showPromptButton = false,
  onStyleRated,
  tags,
}) => {
  const { analysis, isLoading, analysisError } = useCreativeAnalysis(image);

  return (
    <div className="banner-detail-panel w-full lg:w-96 flex flex-col bg-white min-h-0">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-bold text-gray-900">バナー詳細</h3>
            {image.source_image_id && (
              <span className="rounded-md bg-indigo-50 px-2 py-1 text-xs font-semibold text-indigo-700">
                編集版
              </span>
            )}
          </div>
          {image.style_id && image.style_name && (
            <div className="flex items-center gap-2">
              <StyleInfoBadge styleId={image.style_id} styleName={image.style_name} />
              <StyleFeedbackButtons
                imageId={image.id}
                styleId={image.style_id}
                currentRating={image.style_rating ?? null}
                source="banner_detail_panel"
                onRated={onStyleRated}
              />
            </div>
          )}
          {tags && <div className="mt-1">{tags}</div>}
        </div>
        <button
          onClick={onClose}
          aria-label="バナー詳細を閉じる"
          className="h-8 w-8 flex items-center justify-center hover:bg-gray-100 rounded-md transition-colors"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {/* AI Creative Analysis Section: render the frame before the async result arrives. */}
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-base">&#x1F4CA;</span>
            <span className="text-sm font-semibold text-gray-900">AI効果予測</span>
            <EffectPredictionTooltip />
          </div>

          {isLoading ? (
            <CreativeAnalysisLoadingIndicator />
          ) : analysisError ? (
            <p className="text-sm text-gray-500">{analysisError}</p>
          ) : analysis ? (
            <>
              {/* Overall Score */}
              <div className="flex items-center gap-3 mb-5">
                <div
                  className="w-14 h-14 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${getScoreColor(analysis.overall_score)}15` }}
                >
                  <span
                    className="text-2xl font-bold"
                    style={{ color: getScoreColor(analysis.overall_score) }}
                  >
                    {analysis.overall_score.toFixed(1)}
                  </span>
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-700">総合評価</span>
                    <span className="text-xs text-gray-400">/ 10</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2.5">
                    <div
                      className="h-2.5 rounded-full transition-all duration-500"
                      style={{
                        width: `${(analysis.overall_score / 10) * 100}%`,
                        backgroundColor: getScoreColor(analysis.overall_score),
                      }}
                    />
                  </div>
                </div>
              </div>

              <ScoreBar label="インプレッション" score={analysis.scores.impression} />
              <ScoreBar label="視認性" score={analysis.scores.visibility} />
              <ScoreBar label="理解度" score={analysis.scores.comprehension} />
              <ScoreBar label="コピー" score={analysis.scores.copy} />
              <ScoreBar label="行動促進" score={analysis.scores.action} />

              {analysis.feedback && (
                <div className="mt-4 pt-3 border-t border-gray-100">
                  <div className="flex items-start gap-2">
                    <span className="text-sm mt-0.5">&#x1F4AC;</span>
                    <p className="text-sm text-gray-600 leading-relaxed">{analysis.feedback}</p>
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-gray-500">分析結果がありません</p>
          )}
        </div>

        {/* Creative Intent Section: keep this frame stable during analysis too.
            スタイル名は非正規化データ（image.style_name）で分析結果に依存しないため、
            分析の取得中/失敗時でも「使用スタイル」行だけは常に見えるようにする。 */}
        {isLoading ? (
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-base">&#x1F4CD;</span>
              <span className="text-sm font-semibold text-gray-900">制作意図</span>
            </div>
            <div className="space-y-3">
              <CreativeIntentStyleRow styleName={image.style_name} />
              <CreativeAnalysisLoadingIndicator label="AIが制作意図を分析中です..." />
            </div>
          </div>
        ) : analysisError ? (
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-base">&#x1F4CD;</span>
              <span className="text-sm font-semibold text-gray-900">制作意図</span>
            </div>
            <div className="space-y-3">
              <CreativeIntentStyleRow styleName={image.style_name} />
              <p className="text-sm text-gray-500">制作意図を取得できませんでした</p>
            </div>
          </div>
        ) : (
          <CreativeIntentSection analysis={analysis} styleName={image.style_name} />
        )}

        {/* Action Buttons */}
        <div className="space-y-2 pt-2">
          {/* Row 1: Use Prompt + Use Image */}
          <div className="grid grid-cols-2 gap-2">
            {showPromptButton && onUsePrompt && (
              <button
                onClick={onUsePrompt}
                className="flex items-center justify-center gap-1.5 bg-blue-500 hover:bg-blue-600 text-white py-2.5 px-3 rounded-md transition-colors text-sm font-medium"
              >
                <Copy className="w-4 h-4" />
                プロンプトを使う
              </button>
            )}
            <button
              onClick={onUseImage}
              className={`flex items-center justify-center gap-1.5 bg-green-500 hover:bg-green-600 text-white py-2.5 px-3 rounded-md transition-colors text-sm font-medium ${
                showPromptButton && onUsePrompt ? '' : 'col-span-2'
              }`}
            >
              <Image className="w-4 h-4" />
              画像を使う
            </button>
          </div>

          {onGenerateStyleVariations && (
            <button
              onClick={onGenerateStyleVariations}
              className="w-full flex items-center justify-center gap-1.5 bg-amber-500 hover:bg-amber-600 text-white py-2.5 px-3 rounded-md transition-colors text-sm font-medium"
            >
              <Sparkles className="w-4 h-4" />
              このスタイルで複数枚作る
            </button>
          )}

          {onAutoAbTest && (
            <button
              onClick={onAutoAbTest}
              className="w-full flex items-center justify-center gap-1.5 bg-primary hover:opacity-90 text-white py-2.5 px-3 rounded-md transition-colors text-sm font-medium"
              data-track="banner-auto-ab-test"
            >
              <FlaskConical className="w-4 h-4" />
              オートABテスト
            </button>
          )}

          {/* Row 2: Image Edit (unified) */}
          <button
            onClick={onImageEdit}
            className="w-full flex items-center justify-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white py-2.5 px-3 rounded-md transition-colors text-sm font-medium"
          >
            <Pencil className="w-4 h-4" />
            画像を編集
          </button>

          {/* Row 3: Resize + Convert to Video (2-column) */}
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={onResize}
              className="flex items-center justify-center gap-1.5 border border-gray-300 hover:bg-gray-50 text-gray-700 py-2.5 px-3 rounded-md transition-colors text-sm font-medium"
            >
              <Maximize2 className="w-4 h-4" />
              マジックリサイズ
            </button>
            {onConvertToVideo && (
              <button
                onClick={onConvertToVideo}
                disabled={videoActionDisabled}
                className="flex items-center justify-center gap-1.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-wait text-white py-2.5 px-3 rounded-md transition-colors text-sm font-medium"
              >
                <Video className="w-4 h-4" />
                {videoActionLabel}
              </button>
            )}
          </div>

          {/* Row 4: Download + Delete */}
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={onDownload}
              className="flex items-center justify-center gap-1.5 border border-gray-300 hover:bg-gray-50 text-gray-700 py-2 px-3 rounded-md transition-colors text-xs font-medium"
            >
              <Download className="w-3.5 h-3.5" />
              ダウンロード
            </button>
            <button
              onClick={onDelete}
              className="flex items-center justify-center gap-1.5 border border-red-300 text-red-600 hover:bg-red-50 py-2 px-3 rounded-md transition-colors text-xs font-medium"
            >
              <Trash2 className="w-3.5 h-3.5" />
              削除
            </button>
          </div>

          {/* Additional image tool */}
          {onLayers && (
            <button
              onClick={onLayers}
              className="w-full flex items-center justify-center gap-1.5 border border-gray-300 hover:bg-gray-50 text-gray-700 py-2.5 px-3 rounded-md transition-colors text-sm font-medium"
            >
              <Layers className="w-4 h-4" />
              レイヤーに分解 / PSD 書き出し
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BannerDetailPanel;
